import { v, type Infer } from "convex/values";

export const takeoffPdfReportModeValidator = v.union(
  v.literal("executive_summary"),
  v.literal("full")
);

export const takeoffExportOptionsValidator = v.object({
  includeSchedule: v.boolean(),
  includeTags: v.boolean(),
  includeMeasurements: v.boolean(),
  includeColumns: v.array(v.string()),
  reportMode: takeoffPdfReportModeValidator,
  includeCosts: v.optional(v.boolean()),
});

export const revPilotCombinedPdfExportOptionsValidator = v.object({
  includeTakeoffRevisions: v.boolean(),
  includeQuantityDeltas: v.boolean(),
  includeTradeNarratives: v.boolean(),
  takeoffOptions: v.object({
    includeSchedule: v.boolean(),
    includeTags: v.boolean(),
    includeMeasurements: v.boolean(),
    includeColumns: v.array(v.string()),
    reportMode: takeoffPdfReportModeValidator,
  }),
});

// Selectable schedule columns for the FieldPilot export schedule table. "Task Name"
// (the tag display name) is always rendered, so it is not part of the selectable set.
export const FIELD_PILOT_SCHEDULE_COLUMN_KEYS = [
  "duration",
  "start",
  "finish",
  "expected_pct",
  "actual_pct",
  "status",
] as const;

export type FieldPilotScheduleColumnKey =
  (typeof FIELD_PILOT_SCHEDULE_COLUMN_KEYS)[number];

export const FIELD_PILOT_SCHEDULE_COLUMN_LABELS: Record<
  FieldPilotScheduleColumnKey,
  string
> = {
  duration: "Duration",
  start: "Start",
  finish: "Finish",
  expected_pct: "Expected %",
  actual_pct: "Actual %",
  status: "Late / Early",
};

export const DEFAULT_FIELD_PILOT_SCHEDULE_COLUMNS: FieldPilotScheduleColumnKey[] = [
  "duration",
  "start",
  "finish",
  "expected_pct",
  "actual_pct",
  "status",
];

export const fieldPilotScheduleColumnValidator = v.union(
  v.literal("duration"),
  v.literal("start"),
  v.literal("finish"),
  v.literal("expected_pct"),
  v.literal("actual_pct"),
  // Deprecated: kept so persisted export artifacts with the old default still validate.
  v.literal("variance"),
  v.literal("status")
);

// Per-status export layout: "combined" renders the normal report plus one section per status;
// "single" filters the whole report down to a single chosen status.
export const fieldPilotStatusSectionModeValidator = v.union(
  v.literal("combined"),
  v.literal("single")
);

export type FieldPilotStatusSectionMode = Infer<
  typeof fieldPilotStatusSectionModeValidator
>;

// How plan-markup measurements are colorized: by their current status (default — shows install
// progress) or by their tag (TakeoffPilot-style, to visually distinguish tags).
export const fieldPilotColorizeByValidator = v.union(
  v.literal("status"),
  v.literal("tag")
);

export type FieldPilotColorizeBy = Infer<typeof fieldPilotColorizeByValidator>;

// What the optional line(s) under each plan-markup label show. "off" = name only;
// "next" = the next status the item must reach + its due date ("→ Installed by 7/4"),
// or the current completion status + associated date when the item is already final
// ("Inspected by 7/10"); "current" = the item's current status name; "all" = every
// dated milestone, each on its own line inside the label ("Installed by 7/4\nInspected by 7/10").
export const fieldPilotLabelAnnotationValidator = v.union(
  v.literal("off"),
  v.literal("next"),
  v.literal("current"),
  v.literal("all")
);

export type FieldPilotLabelAnnotation = Infer<
  typeof fieldPilotLabelAnnotationValidator
>;

export const fieldPilotPdfExportOptionsValidator = v.object({
  saveAsDailyLog: v.boolean(),
  logDateKey: v.string(),
  timeZone: v.optional(v.string()),
  // Whether to render the schedule table at all. Defaults to true for back-compat.
  includeSchedule: v.optional(v.boolean()),
  // Selected schedule columns (keys from FIELD_PILOT_SCHEDULE_COLUMN_KEYS).
  scheduleColumns: v.optional(v.array(fieldPilotScheduleColumnValidator)),
  // Whether to render the per-tag measurement breakdown table.
  includeMeasurementBreakdown: v.optional(v.boolean()),
  // Whether to render measurement markup on the plan pages.
  includePlanMarkup: v.optional(v.boolean()),
  // Whether to render the per-tag expected-vs-actual progress bars section.
  includeProgressBars: v.optional(v.boolean()),
  // Whether to render a full-page Gantt chart.
  includeGanttChart: v.optional(v.boolean()),
  // Deprecated per-status section mode (kept for back-compat with already-saved artifacts).
  statusSectionMode: v.optional(fieldPilotStatusSectionModeValidator),
  // Optional single-status filter: limits plan markup to items at that status OR with a milestone
  // for it, annotating each with that status's due date.
  statusFilterId: v.optional(v.id("fieldPilotStatuses")),
  // Whether plan-markup measurements are colorized by status (default) or by tag.
  colorizeBy: v.optional(fieldPilotColorizeByValidator),
  // Whether measurement value text (e.g. "100 SQFT") is shown beneath each plan-markup label.
  showMeasurementValues: v.optional(v.boolean()),
  // Optional annotation line(s) under each plan-markup label (off / next / current / all milestones).
  labelAnnotation: v.optional(fieldPilotLabelAnnotationValidator),
});

export const specPilotSubmittalPdfExportOptionsValidator = v.object({
  version: v.union(v.literal("generated"), v.literal("edited")),
});

export const specPilotTrackerPdfExportScopeValidator = v.union(
  v.literal("single"),
  v.literal("all")
);

export const specPilotTrackerTypeValidator = v.union(
  v.literal("warranty"),
  v.literal("lead_time"),
  v.literal("attic_stock"),
  v.literal("custom")
);

export const specPilotTrackerPdfExportOptionsValidator = v.object({
  scope: specPilotTrackerPdfExportScopeValidator,
  trackerType: v.optional(specPilotTrackerTypeValidator),
  customTrackerName: v.optional(v.string()),
});

// Temporary compatibility shape for existing exportArtifacts created before
// options were typed. Remove in the final CON-130 schema cleanup after backfill.
export const legacyExportArtifactOptionsValidator = v.object({});

export const exportArtifactOptionsValidator = v.union(
  takeoffExportOptionsValidator,
  revPilotCombinedPdfExportOptionsValidator,
  fieldPilotPdfExportOptionsValidator,
  specPilotSubmittalPdfExportOptionsValidator,
  specPilotTrackerPdfExportOptionsValidator,
  legacyExportArtifactOptionsValidator
);

export type TakeoffExportOptions = Infer<typeof takeoffExportOptionsValidator>;
export type FieldPilotPdfExportOptions = Infer<typeof fieldPilotPdfExportOptionsValidator>;
export type SpecPilotSubmittalPdfExportOptions = Infer<
  typeof specPilotSubmittalPdfExportOptionsValidator
>;
export type SpecPilotTrackerPdfExportOptions = Infer<
  typeof specPilotTrackerPdfExportOptionsValidator
>;
